import { useCallback, useState } from 'react';
import { supabase } from '../lib/supabase';
import { getErrorMessage } from '../lib/errors';
import { error as logError } from '../lib/logger';

// Supabase function invoke error shape
type InvokeError = {
  message?: string;
  context?: {
    body?: string;
    text?: string;
  };
};

interface UseStatusTransitionResult {
  transitionStatus: (orderId: string, newStatusId: string, changedBy: string, notes?: string) => Promise<boolean>;
  isLoading: boolean;
  error: string | null;
  lastResult: { success: boolean; notificationsCreated?: number } | null;
}

export function useStatusTransition(): UseStatusTransitionResult {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastResult, setLastResult] = useState<{ success: boolean; notificationsCreated?: number } | null>(null);

  const transitionStatus = useCallback(async (
    orderId: string,
    newStatusId: string,
    changedBy: string,
    notes?: string
  ): Promise<boolean> => {
    setIsLoading(true);
    setError(null);

    try {
      const { data, error: invokeError } = await supabase.functions.invoke('notify-status-change', {
        body: {
          order_id: orderId,
          new_status_id: newStatusId,
          changed_by: changedBy,
          notes: notes || null,
        },
      });

      if (invokeError) {
        // Try every possible place the real error could be
        const errData = data as Record<string, unknown> | null;
        const ctx = (invokeError as InvokeError)?.context;
        const body = ctx?.body || ctx?.text || '';
        let parsed: Record<string, unknown> | null = null;
        try { parsed = JSON.parse(body); } catch { /* not JSON */ }

        const msg = String(
          errData?.error ||
          parsed?.error ||
          parsed?.message ||
          invokeError.message ||
          'Failed to transition status'
        );

        logError('[useStatusTransition]', { msg, invokeError, data, ctx, parsed });
        setError(msg);
        return false;
      }

      setLastResult({ success: true, notificationsCreated: data?.notifications_created });
      return true;
    } catch (err) {
      const msg = getErrorMessage(err) || 'Failed to transition status';
      setError(msg);
      return false;
    } finally {
      setIsLoading(false);
    }
  }, []);

  return { transitionStatus, isLoading, error, lastResult };
}